import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, ChevronRight, Wind, User, Eye, Hand, Feather, Home } from 'lucide-react';

/**
 * Unified card button style for relationship hub pages
 */
const ToolCard = ({ icon: Icon, title, description, onClick, iconBg = 'bg-[#1D6B6B]/10', iconColor = 'text-[#1D6B6B]', delay = 0 }) => (
    <button
        onClick={onClick}
        className="w-full p-5 rounded-2xl bg-white border border-[#E8E1D6] text-left
                 hover:border-[#1D6B6B]/40 hover:shadow-md transition-all duration-200
                 flex items-center gap-4"
        style={{ animationDelay: `${delay}ms` }}
    >
        <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#1D3D3D] mb-0.5">{title}</p>
            {description && <p className="text-sm text-[#5A7A7A]">{description}</p>}
        </div>
        <ChevronRight className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
    </button>
);

/**
 * Simple Co-Regulation Tool for Relationship Hub
 * A working version without the complex brand components that cause issues
 */
const CoRegulationSimple = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState('intro');
    const [currentStepIndex, setCurrentStepIndex] = useState(0);

    const steps = [
        { id: 'breath', icon: Wind, title: 'Breath', prompt: 'Take one slow breath together. In… and out. Let your breathing sync.' },
        { id: 'body', icon: User, title: 'Body', prompt: 'Both of you: let your shoulders soften. Unclench your jaw. Place a hand on your chest if it feels comforting.' },
        { id: 'orientation', icon: Eye, title: 'Orientation', prompt: 'Look around the room together. Name three things you can both see. Let your eyes meet for a moment.' },
        { id: 'touch', icon: Hand, title: 'Gentle Touch', prompt: 'If comfortable, try holding hands, or placing a hand on each other\'s shoulder. Small touches can bridge big feelings.' },
        { id: 'softening', icon: Feather, title: 'Softening', prompt: 'Notice this moment. You don\'t need to fix anything — just be here together.' },
    ];

    const handleNext = () => {
        if (currentStepIndex < steps.length - 1) {
            setCurrentStepIndex(prev => prev + 1);
            setStep('step');
        } else {
            setStep('complete');
        }
    };

    const handleBack = () => {
        if (step === 'complete') {
            setStep('step');
        } else if (currentStepIndex > 0) {
            setCurrentStepIndex(prev => prev - 1);
        } else {
            setStep('intro');
        }
    };

    const handleRestart = () => {
        setStep('intro');
        setCurrentStepIndex(0);
    };

    // Intro screen
    if (step === 'intro') {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4]">
                <header className="sticky top-0 z-50 glass-header">
                    <div className="max-w-xl mx-auto px-6 py-4 flex items-center justify-between">
                        <button 
                            onClick={() => navigate('/relationship-hub')}
                            className="p-2 -ml-2 rounded-full hover:bg-[#FFBB98]/40 transition-colors text-[#7D8E95] hover:text-[#1D6B6B]"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <h1 className="text-lg font-semibold text-[#1D6B6B]">Co-Regulation</h1>
                        <div className="w-9" />
                    </div>
                </header>

                <main className="max-w-xl mx-auto px-6 py-12">
                    <div className="text-center">
                        <div className="w-20 h-20 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center mx-auto mb-6">
                            <Heart className="w-10 h-10 text-[#1D6B6B]" />
                        </div>
                        
                        <h2 className="text-2xl font-serif text-[#1D3D3D] mb-4">
                            Relationship Co-Regulation
                        </h2>
                        
                        <p className="text-[#5A7A7A] max-w-md mx-auto leading-relaxed mb-3">
                            When emotions feel big or fast, these gentle steps can help both of you settle together.
                        </p>
                        
                        <p className="text-sm text-[#7D8E95] max-w-sm mx-auto mb-10">
                            There's no rush. Go at your own pace.
                        </p>
                        
                        <button
                            onClick={() => setStep('step')}
                            className="w-full max-w-sm mx-auto py-4 px-6 rounded-2xl bg-[#1D6B6B] text-white font-semibold
                                     hover:bg-[#145555] transition-all shadow-md flex items-center justify-center gap-2"
                        >
                            Begin Together
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    // Steps screen
    if (step === 'step') {
        const currentStep = steps[currentStepIndex];
        const StepIcon = currentStep.icon;

        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4]">
                <header className="sticky top-0 z-50 glass-header">
                    <div className="max-w-xl mx-auto px-6 py-4 flex items-center justify-between">
                        <button 
                            onClick={handleBack}
                            className="p-2 -ml-2 rounded-full hover:bg-[#FFBB98]/40 transition-colors text-[#7D8E95] hover:text-[#1D6B6B]"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <h1 className="text-lg font-semibold text-[#1D6B6B]">{currentStep.title}</h1>
                        <div className="w-9" />
                    </div>
                </header>

                <main className="max-w-xl mx-auto px-6 py-8">
                    {/* Progress dots */}
                    <div className="flex justify-center gap-2 mb-8">
                        {steps.map((s, i) => (
                            <div 
                                key={s.id}
                                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                                    i === currentStepIndex ? 'bg-[#1D6B6B]' : 
                                    i < currentStepIndex ? 'bg-[#1D6B6B]/50' : 'bg-[#E8E1D6]'
                                }`}
                            />
                        ))}
                    </div>

                    {/* Step card */}
                    <div className="bg-white rounded-2xl border border-[#E8E1D6] shadow-sm p-8 mb-8">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-xl bg-[#1D6B6B]/10 flex items-center justify-center mx-auto mb-6">
                                <StepIcon className="w-8 h-8 text-[#1D6B6B]" />
                            </div>
                            
                            <p className="text-lg text-[#1D3D3D] leading-relaxed">
                                {currentStep.prompt}
                            </p>
                        </div>
                    </div>

                    {/* Navigation */}
                    <div className="space-y-3">
                        <button
                            onClick={handleNext}
                            className="w-full py-4 px-6 rounded-2xl bg-[#1D6B6B] text-white font-semibold
                                     hover:bg-[#145555] transition-all shadow-md flex items-center justify-center gap-2"
                        >
                            {currentStepIndex < steps.length - 1 ? 'Continue' : 'Complete'}
                            <ChevronRight className="w-5 h-5" />
                        </button>
                        
                        <button
                            onClick={() => navigate('/relationship-hub')}
                            className="w-full py-3 text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                        >
                            Come back later
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    // Complete screen
    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex items-center justify-center p-6">
            <div className="max-w-md w-full text-center">
                <div className="w-24 h-24 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center mx-auto mb-8">
                    <Heart className="w-12 h-12 text-[#1D6B6B]" />
                </div>
                
                <h2 className="text-2xl font-serif text-[#1D3D3D] mb-4">
                    You did this together
                </h2>
                <p className="text-lg text-[#5A7A7A] mb-10">
                    Take a moment to acknowledge each other. 
                    Connection happens one step at a time.
                </p>
                
                <div className="space-y-3">
                    <button 
                        onClick={handleRestart}
                        className="w-full py-4 px-6 rounded-2xl bg-[#1D6B6B] text-white font-semibold
                                 hover:bg-[#145555] transition-all shadow-md"
                    >
                        Do Another Round
                    </button>
                    <button 
                        onClick={() => navigate('/communicate')}
                        className="w-full py-4 px-6 rounded-2xl bg-white border border-[#E8E1D6] text-[#1D3D3D] font-semibold
                                 hover:border-[#1D6B6B]/40 hover:shadow-md transition-all"
                    >
                        Need Words?
                    </button>
                    <button 
                        onClick={() => navigate('/relationship-hub')}
                        className="w-full py-3 text-[#7D8E95] font-medium hover:text-[#1D6B6B] transition-colors flex items-center justify-center gap-2"
                    >
                        <Home className="w-4 h-4" />
                        Back to Relationship Hub
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CoRegulationSimple;
